import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import styles from "./index.module.less";
import { Input } from "antd";
import { withErrorBoundary } from "@/common/ErrorBoundary/hoc";
import { useState, useEffect, useRef, useMemo } from "react";
const InputSearch = (props) => {
    const { data, // 使用 mock 数据作为默认值
    fieldName, form, placeholder = "请输入", labelKey = "name", valueKey = "id", maxLength = 50, onSelect, showCount = true, onlyValue = true, inputName, } = props;
    const [inputValue, setInputValue] = useState("");
    const [showDropdown, setShowDropdown] = useState(false);
    const [focusedIndex, setFocusedIndex] = useState(-1);
    const dropdownRef = useRef(null);
    const inputRef = useRef(null);
    const dropdownListRef = useRef(null);
    // 文本标准化函数 - 智能处理不同语言文字
    const normalizeText = (text) => {
        return text
            .toLowerCase()
            // 去除越南语重音符号，但保留中文字符
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '') // 去除组合重音符号
            .replace(/đ/g, 'd') // 特殊处理越南语字母 đ
            .replace(/Đ/g, 'd')
            // 去除标点符号和特殊字符，但保留中文字符、字母、数字和空格
            .replace(/[^\u4e00-\u9fff\w\s]/g, ' ') // 保留中文字符范围
            // 合并多个空格为一个
            .replace(/\s+/g, ' ')
            .trim();
    };
    // 智能模糊匹配函数
    const fuzzyMatch = (text, searchText) => {
        if (!searchText.trim())
            return false;
        const normalizedText = normalizeText(text);
        const normalizedSearch = normalizeText(searchText);
        // 将搜索文本分词
        const searchWords = normalizedSearch.split(' ').filter(word => word.length > 0);
        // 所有搜索词都必须在目标文本中找到
        return searchWords.every(word => normalizedText.includes(word));
    };
    // 过滤和匹配数据
    const filteredData = useMemo(() => {
        if (!inputValue.trim()) {
            return [];
        }
        const filtered = data?.filter((item) => {
            const label = item[labelKey]?.toString() || "";
            return fuzzyMatch(label, inputValue);
        }) || [];
        return filtered;
    }, [data, inputValue, labelKey]);
    // 当筛选结果变化时，滚动到顶部
    useEffect(() => {
        if (dropdownListRef.current && filteredData.length > 0) {
            // 使用 setTimeout 确保 DOM 已经更新
            setTimeout(() => {
                if (dropdownListRef.current) {
                    dropdownListRef.current.scrollTop = 0;
                }
            }, 0);
        }
    }, [filteredData]);
    // 高亮匹配文字的函数
    const highlightMatch = (text, searchText) => {
        if (!searchText.trim())
            return text;
        // 将搜索文本分词
        const searchWords = searchText.trim().split(/\s+/).filter(word => word.length > 0);
        if (searchWords.length === 0)
            return text;
        // 对每个搜索词进行标准化处理，但保持原始大小写用于显示
        const normalizedSearchWords = searchWords.map(word => normalizeText(word));
        const normalizedText = normalizeText(text);
        // 找出所有匹配的位置
        const matches = [];
        normalizedSearchWords.forEach(normalizedWord => {
            if (!normalizedWord)
                return;
            let startIndex = 0;
            while (startIndex < normalizedText.length) {
                const matchIndex = normalizedText.indexOf(normalizedWord, startIndex);
                if (matchIndex === -1)
                    break;
                matches.push({
                    start: matchIndex,
                    end: matchIndex + normalizedWord.length,
                    word: normalizedWord
                });
                startIndex = matchIndex + 1;
            }
        });
        if (matches.length === 0)
            return text;
        // 按起始位置排序并合并重叠的匹配
        matches.sort((a, b) => a.start - b.start);
        const mergedMatches = [];
        for (const match of matches) {
            if (mergedMatches.length === 0) {
                mergedMatches.push(match);
            }
            else {
                const lastMatch = mergedMatches[mergedMatches.length - 1];
                if (match.start <= lastMatch.end) {
                    // 合并重叠的匹配
                    lastMatch.end = Math.max(lastMatch.end, match.end);
                }
                else {
                    mergedMatches.push(match);
                }
            }
        }
        // 构建高亮结果
        const result = [];
        let lastIndex = 0;
        for (let i = 0; i < mergedMatches.length; i++) {
            const match = mergedMatches[i];
            // 添加匹配前的文本
            if (match.start > lastIndex) {
                result.push(text.slice(lastIndex, match.start));
            }
            // 添加高亮的匹配文本
            result.push(_jsx("span", { className: styles.highlight, children: text.slice(match.start, match.end) }, `highlight-${i}`));
            lastIndex = match.end;
        }
        // 添加最后剩余的文本
        if (lastIndex < text.length) {
            result.push(text.slice(lastIndex));
        }
        return result;
    };
    // 处理输入变化
    const handleInputChange = (e) => {
        const value = e.target.value;
        setInputValue(value);
        setShowDropdown(true);
        setFocusedIndex(-1);
        // 没有匹配数据时，根据onlyValue决定返回格式
        const formValue = onlyValue ? value : { id: null, value: value };
        form.setFieldsValue({ [fieldName]: formValue });
        form.validateFields([fieldName]); // 主动触发校验
        // 立即滚动到顶部
        setTimeout(() => {
            if (dropdownListRef.current) {
                dropdownListRef.current.scrollTop = 0;
            }
        }, 10);
    };
    // 处理失去焦点时的自动约束
    const handleInputBlur = () => {
        // 输入完成后自动去除首尾空格
        const trimmedValue = inputValue.trim();
        if (trimmedValue !== inputValue) {
            setInputValue(trimmedValue);
            // 没有匹配数据时，根据onlyValue决定返回格式
            const formValue = onlyValue ? trimmedValue : { id: null, value: trimmedValue };
            form.setFieldsValue({ [fieldName]: formValue });
            form.validateFields([fieldName]); // 主动触发校验
        }
    };
    // 处理选择项目
    const handleSelectItem = (item) => {
        const selectedValue = item[labelKey];
        setInputValue(selectedValue);
        setShowDropdown(false);
        setFocusedIndex(-1);
        // 根据onlyValue决定返回格式
        const formValue = onlyValue
            ? selectedValue
            : {
                id: item[valueKey],
                value: selectedValue
            };
        // 更新表单字段
        form.setFieldsValue({ [fieldName]: formValue });
        // 调用回调函数
        onSelect?.(formValue);
    };
    // 处理键盘事件
    const handleKeyDown = (e) => {
        if (!showDropdown || filteredData.length === 0)
            return;
        switch (e.key) {
            case "ArrowDown":
                e.preventDefault();
                setFocusedIndex((prev) => prev < filteredData.length - 1 ? prev + 1 : 0);
                break;
            case "ArrowUp":
                e.preventDefault();
                setFocusedIndex((prev) => prev > 0 ? prev - 1 : filteredData.length - 1);
                break;
            case "Enter":
                e.preventDefault();
                if (focusedIndex >= 0 && focusedIndex < filteredData.length) {
                    handleSelectItem(filteredData[focusedIndex]);
                }
                break;
            case "Escape":
                setShowDropdown(false);
                setFocusedIndex(-1);
                break;
        }
    };
    // 处理点击外部关闭下拉框
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current &&
                !dropdownRef.current.contains(event.target)) {
                setShowDropdown(false);
                setFocusedIndex(-1);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);
    // 初始化表单值
    useEffect(() => {
        const formValue = form.getFieldValue(fieldName);
        if (formValue) {
            // 根据onlyValue判断如何获取显示值
            let displayValue = '';
            if (onlyValue) {
                // onlyValue为true时，formValue就是字符串
                displayValue = formValue;
            }
            else {
                // onlyValue为false时，formValue是对象，取value字段
                displayValue = typeof formValue === 'object' && formValue.value ? formValue.value : formValue;
            }
            setInputValue(displayValue);
        }
    }, [form.getFieldValue(fieldName), fieldName, onlyValue]);
    return (_jsxs("div", { className: styles.selectSearch, ref: dropdownRef, children: [_jsx(Input, { ref: inputRef, className: `${styles.selectSearchInput} ${inputName} ${showDropdown && filteredData.length > 0 ? styles.showDropdownInput : ''}`, value: inputValue, placeholder: placeholder, onChange: handleInputChange, onFocus: () => {
                    // 聚焦时如果有输入内容就显示下拉框
                    if (inputValue.trim()) {
                        setShowDropdown(true);
                    }
                }, onBlur: handleInputBlur, onKeyDown: handleKeyDown, maxLength: maxLength, showCount: showCount }), showDropdown && filteredData.length > 0 && (_jsx("div", { className: styles.dropdown, children: _jsx("div", { className: styles.dropdownList, ref: dropdownListRef, children: filteredData.map((item, index) => (_jsx("div", { className: `${styles.dropdownItem} ${index === focusedIndex ? styles.focused : ""}`, onClick: () => handleSelectItem(item), onMouseEnter: () => setFocusedIndex(index), children: highlightMatch(item[labelKey], inputValue) }, item[valueKey] || index))) }) }))] }));
};
export default withErrorBoundary(InputSearch);
